import { useState } from 'react';
import { ArrowLeft, Cake, CheckCircle2 } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useLanguage } from '@/contexts/AppContext';

interface BirthdayVerificationPageProps {
  onBack: () => void;
  onVerificationComplete: (birthday: string) => void;
  currentBirthday?: string;
}

const translations = {
  ko: {
    title: '생일 인증',
    subtitle: '생일을 인증하고 생일 달에 추가 할인을 받으세요',
    birthdayLabel: '생일',
    birthdayPlaceholder: 'YYYY-MM-DD',
    verify: '인증하기',
    currentVerification: '현재 인증 정보',
    birthdayMonth: '생일 할인 적용',
    changeVerification: '생일 정보 변경',
    verified: '인증 완료',
    benefit: '생일 달에는 모든 제휴 매장에서 추가 할인이 적용됩니다',
    back: '뒤로'
  },
  es: {
    title: 'Verificación de Cumpleaños',
    subtitle: 'Verifica tu cumpleaños y recibe descuentos adicionales en tu mes',
    birthdayLabel: 'Fecha de Nacimiento',
    birthdayPlaceholder: 'AAAA-MM-DD',
    verify: 'Verificar',
    currentVerification: 'Verificación Actual',
    birthdayMonth: 'descuento de cumpleaños activo',
    changeVerification: 'Cambiar Información',
    verified: 'Verificado',
    benefit: 'Recibe descuentos adicionales en todas las tiendas durante tu mes de cumpleaños',
    back: 'Atrás'
  },
  en: {
    title: 'Birthday Verification',
    subtitle: 'Verify your birthday and receive extra discounts during your birth month',
    birthdayLabel: 'Birthday',
    birthdayPlaceholder: 'YYYY-MM-DD',
    verify: 'Verify',
    currentVerification: 'Current Verification',
    birthdayMonth: 'birthday discount active',
    changeVerification: 'Change Information',
    verified: 'Verified',
    benefit: 'Receive additional discounts at all partner stores during your birthday month',
    back: 'Back'
  }
};

export function BirthdayVerificationPage({
  onBack,
  onVerificationComplete,
  currentBirthday,
}: BirthdayVerificationPageProps) {
  const { language } = useLanguage();
  const [birthday, setBirthday] = useState(currentBirthday || '');
  const [isEditing, setIsEditing] = useState(!currentBirthday);

  const t = translations[language];

  const handleVerifyBirthday = () => {
    if (birthday) {
      onVerificationComplete(birthday);
    }
  };

  const getBirthdayMonth = (date: string) => {
    const month = new Date(date).getMonth() + 1;
    return month;
  };

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <div className="bg-gradient-to-r from-warning to-warning-light sticky top-0 z-50">
        <div className="flex items-center gap-4 p-[min(5vw,1.25rem)]">
          <button
            onClick={onBack}
            className="p-2 hover:bg-white/10 rounded-xl transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-white" />
          </button>
          <h1 className="text-[calc(1.125rem+0.25vw)] font-bold text-white flex-1">
            {t.title}
          </h1>
        </div>
      </div>

      <div className="p-[min(5vw,1.25rem)] pb-[min(8vw,2rem)]">
        {/* Subtitle */}
        <p className="text-[calc(0.875rem+0.125vw)] text-muted-foreground mb-[min(6vw,2rem)] text-center">
          {t.subtitle}
        </p>

        {/* Current Verification (if exists) */}
        {currentBirthday && !isEditing && (
          <Card className="p-[min(5vw,1.75rem)] bg-background rounded-3xl shadow-lg mb-[min(5vw,1.5rem)] border-2 border-warning-light/20">
            <div className="flex items-center gap-3 mb-[min(4vw,1.5rem)]">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-primary to-primary-hover">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-[calc(1rem+0.25vw)] font-bold text-foreground">
                  {t.currentVerification}
                </h3>
                <div className="flex items-center gap-1.5 mt-1">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  <span className="text-[calc(0.75rem+0.125vw)] text-primary font-medium">
                    {t.verified}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-warning-lighter/20 to-warning/10 rounded-2xl p-4 flex items-center gap-3 mb-4">
              <CheckCircle2 className="h-8 w-8 text-primary shrink-0" />
              <div className="flex-1">
                <p className="text-[calc(0.875rem+0.25vw)] font-medium text-foreground">
                  {currentBirthday}
                </p>
                <p className="text-[calc(0.7rem+0.125vw)] text-muted-foreground">
                  {getBirthdayMonth(currentBirthday)}{language === 'ko' ? '월' : ''} {t.birthdayMonth}
                </p>
              </div>
            </div>

            <Button
              onClick={() => setIsEditing(true)}
              className="w-full bg-gradient-to-r from-warning to-warning-light hover:from-warning-dark hover:to-warning-light text-white rounded-xl py-3"
            >
              {t.changeVerification}
            </Button>
          </Card>
        )}

        {/* Verification Form */}
        {(!currentBirthday || isEditing) && (
          <Card className="p-[min(5vw,1.75rem)] bg-background rounded-3xl shadow-lg mb-[min(5vw,1.5rem)] border-2 border-warning-light/20">
            <div className="flex items-center gap-3 mb-[min(4vw,1.5rem)]">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-warning to-warning-light">
                <Cake className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-[calc(1rem+0.25vw)] font-bold text-foreground">
                  {t.birthdayLabel}
                </h3>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-[calc(0.875rem+0.125vw)] text-muted-foreground mb-2">
                  {t.birthdayLabel}
                </label>
                <Input
                  type="date"
                  placeholder={t.birthdayPlaceholder}
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                  className="rounded-xl py-6 px-4 bg-muted border-0 text-[calc(0.875rem+0.125vw)]"
                />
              </div>
              
              <Button
                onClick={handleVerifyBirthday}
                disabled={!birthday}
                className="w-full bg-gradient-to-r from-warning to-warning-light hover:from-warning-dark hover:to-warning-light text-white rounded-xl py-6 disabled:opacity-50"
              >
                {t.verify}
              </Button>
            </div>
          </Card>
        )}

        {/* Benefit Info */}
        <Card className="p-[min(5vw,1.75rem)] bg-gradient-to-br from-warning-lighter/20 to-warning/10 rounded-3xl shadow-lg border-2 border-warning/20">
          <div className="flex items-start gap-3">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-warning to-warning-light">
              <Cake className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-[calc(1rem+0.25vw)] font-bold text-foreground mb-2">
                {language === 'ko' ? '생일 혜택' : language === 'es' ? 'Beneficio de Cumpleaños' : 'Birthday Benefit'}
              </h3>
              <p className="text-[calc(0.875rem+0.125vw)] text-muted-foreground">
                {t.benefit}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}